import AntIcon from "@/components/icons/AntIcon";
import { selectStandardProps } from "@/utils/componentUtils";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Card, Col, Form, Input, Row, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { rowGutter } from "../../../../../common/constants";
import { FuelType } from "../../../../contract/enums";
import { FILTER_VEHICLE_FUEL_TYPES_QUERY_PARAMS } from "../../api";
import { FuelNameSource } from "../../enums";
import { CertificateFuelTypeFilterPageRequest } from "../../types";

const colSpan = 6;

interface Props {
  searchParams: CertificateFuelTypeFilterPageRequest;
  onCreateClick: () => void;
}

export const VehicleFuelTypeFilterForm = ({ searchParams, onCreateClick }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form] = Form.useForm<CertificateFuelTypeFilterPageRequest>();

  useEffect(() => {
    form.setFieldsValue({
      keyword: searchParams.keyword,
      fuelTypes: searchParams.fuelTypes,
      fuelNameSources: searchParams.fuelNameSources,
    });
  }, [searchParams, form]);

  const onFilterSubmit = (values: CertificateFuelTypeFilterPageRequest) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...values,
            ...skipInitialFormValues(values, FILTER_VEHICLE_FUEL_TYPES_QUERY_PARAMS),
            pageIndex: undefined,
          },
        }),
      },
      { replace: true },
    );
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("vehicleFuelType.titles.pageTitle")}</h2>}
      extra={
        <Button type="primary" icon={<AntIcon type="plus" />} onClick={onCreateClick}>
          {t("vehicleFuelType.actions.createFuelType")}
        </Button>
      }
    >
      <Form form={form} onFinish={onFilterSubmit} layout="vertical" name="vehicleFuelTypeSearchForm">
        <Row gutter={rowGutter} justify="center">
          <Col span={colSpan}>
            <Form.Item
              name="keyword"
              label={t("common.search")}
              rules={[
                validations.size(
                  validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
                  validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
                ),
              ]}
            >
              <Input allowClear placeholder={t("vehicleFuelType.helpers.filterSearchPlaceholder")} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item name="fuelTypes" label={t("contract.enums.fuelType._label")} rules={[validations.none]}>
              <Select
                {...selectStandardProps}
                mode="multiple"
                maxTagCount="responsive"
                allowClear
                options={Object.keys(FuelType).map((fuelType) => ({
                  value: fuelType,
                  label: t("contract.enums.fuelType." + fuelType),
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="fuelNameSources"
              label={t("vehicle.enums.fuelNameSource._label")}
              rules={[validations.none]}
            >
              <Select
                {...selectStandardProps}
                mode="multiple"
                maxTagCount="responsive"
                allowClear
                options={Object.keys(FuelNameSource).map((fuelNameSource) => ({
                  value: fuelNameSource,
                  label: t("vehicle.enums.fuelNameSource." + fuelNameSource),
                }))}
              />
            </Form.Item>
          </Col>

          <Col flex="110px" className="no-title-space">
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
                {t("common.filter")}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};
